// Pure card-entry logic for Razorpay Custom Checkout — the limits, the MM/YY
// split, and what still blocks submission. Lives beside the fields rather than
// inside them so the component file exports a component and nothing else: a file
// mixing the two cannot preserve state across Fast Refresh, and these are the
// pieces most worth testing without mounting anything.

export interface RazorpayCustomCardFieldValues {
  cardNumber: string
  cardName: string
  expMonth: string
  expYear: string
  cvv: string
  email: string
  contact: string
}

// Expiry is MM / YY — two digits each. CVV is 3 (Visa/Mastercard/RuPay) or 4
// (Amex), so 4 is the ceiling and 3 the floor.
export const EXPIRY_PART_LENGTH = 2
export const CVV_MAX_LENGTH = 4
export const CVV_MIN_LENGTH = 3

// Strip non-digits and cut to length. This runs in the change handler rather
// than being left to the input's maxLength attribute, because maxLength does not
// constrain a programmatic set or a browser autofill — and an attribute-only rule
// cannot be tested under happy-dom, which ignores it entirely.
export const clampNumericInput = (value: string, maxLength: number): string =>
  value.replace(/\D/g, '').slice(0, maxLength)

// The card prints MM/YY as one field and that is how customers type it, but
// Razorpay's createPayment takes expiry_month and expiry_year as separate
// strings. So the box is one input and the split happens here — pure, and
// testable without mounting anything.
export const parseExpiryInput = (raw: string): { month: string; year: string } => {
  const digits = raw.replace(/\D/g, '').slice(0, EXPIRY_PART_LENGTH * 2)
  return {
    month: digits.slice(0, EXPIRY_PART_LENGTH),
    year: digits.slice(EXPIRY_PART_LENGTH),
  }
}

// The separator appears only once there is a year to separate. Appending it as
// soon as a second digit lands makes backspace impossible — the deletion takes
// the slash, this puts it straight back, and the field can never be cleared.
export const formatExpiry = (month: string, year: string): string => {
  const digits = `${month}${year}`
  if (digits.length <= EXPIRY_PART_LENGTH) return digits
  return `${digits.slice(0, EXPIRY_PART_LENGTH)} / ${digits.slice(EXPIRY_PART_LENGTH)}`
}

// Whether the raw card entry is complete enough to submit. Lives here, beside
// the fields and their limits, so the rule and the inputs enforcing it can't
// drift — and so it is testable without mounting the checkout modal.
// Just the four fields the customer can actually see and type.
const isCardFieldsComplete = (values: RazorpayCustomCardFieldValues): boolean =>
  values.cardNumber.replace(/\s+/g, '').length >= 12 &&
  values.expMonth.length === EXPIRY_PART_LENGTH &&
  values.expYear.length === EXPIRY_PART_LENGTH &&
  values.cvv.length >= CVV_MIN_LENGTH

// Razorpay rejects a contact it cannot dial, so a partial number is no number.
export const hasDialableContact = (contact: string): boolean =>
  contact.replace(/\D/g, '').length >= 10

const hasUsableContact = (values: RazorpayCustomCardFieldValues): boolean =>
  hasDialableContact(values.contact)

export const isCardEntryComplete = (
  values: RazorpayCustomCardFieldValues & { email: string; contact: string },
): boolean => isCardFieldsComplete(values) && !!values.email && hasUsableContact(values)

// Why the card step cannot be submitted, named specifically — or undefined when
// it can. Split out from isCardEntryComplete because hiding email and contact
// moved their failure OFF SCREEN: both are still required by createPayment, so an
// account with no phone on file fills the card perfectly and finds the button
// dead. "Enter card details" is a lie there, and the fix is not on this form.
//
// The card comes first deliberately. An unfinished card is what the customer can
// act on right now; the account-level gap is only worth naming once it is the
// only thing left, or it fires on an untouched form and reads as nonsense.
export const getCardEntryBlocker = (values: RazorpayCustomCardFieldValues): string | undefined => {
  if (!isCardFieldsComplete(values)) return 'Enter card details'
  // Phone is per-USER (the session's own 2FA record); billing email is per-account.
  if (!values.email) return 'Add a billing email to your account to pay by card'
  if (!hasUsableContact(values)) return 'Add a phone number to your profile to pay by card'
  return undefined
}

// Only the raw-card createPayment sends customer_phone. Saved cards charge by
// card_mapping_id and the bank rails send nothing, so those paths stay quiet.
// isPhoneSettled is the query's isSuccess: a rejected lookup is not "no phone".
export const shouldShowAddPhonePrompt = ({
  isCardStep,
  isCardMethod,
  payWithSavedCard,
  contact,
  isPhoneSettled,
}: {
  isCardStep: boolean
  isCardMethod: boolean
  payWithSavedCard: boolean
  contact: string
  isPhoneSettled: boolean
}): boolean => {
  if (!isPhoneSettled) return false
  if (hasDialableContact(contact)) return false
  return isCardStep || (isCardMethod && !payWithSavedCard)
}
